import { faListAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import account from '../services/account-service';
import { storageHandler } from '../utils/storage_handler';

const PREFIX = 'HeaderBar';
const classes = {
  root: `${PREFIX}-root`,
  menuButton: `${PREFIX}-menuButton`,
  title: `${PREFIX}-title`
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: { flexGrow: 1 },
  [`& .${classes.menuButton}`]: { marginRight: theme.spacing(2) },
  [`& .${classes.title}`]: { flexGrow: 1 }
}));

export default function HeaderBar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState(null);

  const token = storageHandler.localStorageGet('token');
  const usertype = parseInt(storageHandler.localStorageGet('usertype'), 10);

  const toggleDrawer = (state) => () => setOpen(state);

  // ✅ Filter menu based on usertype
  const menuItems = [
    { text: 'Dashboard', path: '/' },
    { text: 'Package Manager', path: '/packages' },
    { text: 'Deliveries', path: '/deliveries' },
    { text: 'Zoe Shipment Tracking', path: '/zoe-shipment-tracking' },
    { text: 'Purchase Orders', path: '/purchase-orders' },
    { text: 'Zoe Orders', path: '/zoe-orders' },
    { text: 'Reports', path: '/reports' },
    // Zoe Shipment Logs only visible for usertype 2
    ...(usertype === 2 || usertype === 1
      ? [{ text: 'Zoe Shipment Logs', path: '/zoe-shipment-tracking-logs' }]
      : []),
    // User Role Management only visible for usertype 1 (superadmin)
    ...(usertype === 1
      ? [{ text: 'User Role Management', path: '/user-role-management' }]
      : []),
    // Change Logs only visible for usertype 1 (superadmin)
    ...(usertype === 1 ? [{ text: 'Change Logs', path: '/change-logs' }] : []),
    // App Releases (OTA) only visible for usertype 1 (superadmin)
    ...(usertype === 1 ? [{ text: 'App Releases', path: '/app-releases' }] : [])
  ];

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleRedirect = (path) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = async () => {
    const refreshToken = storageHandler.localStorageGet('refresh_token');
    if (refreshToken) {
      try {
        await account.logout(refreshToken);
      } catch {
        // Always clear local state even if API call fails
      }
    }
    storageHandler.localStorageDelete('token');
    storageHandler.localStorageDelete('usertype');
    storageHandler.localStorageDelete('refresh_token');
    handleRedirect('/login');
  };

  return (
    <Root className={classes.root}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            className={classes.menuButton}
            color="inherit"
            aria-label="menu"
            // `enabled` is not a valid prop. MUI forwards unrecognised
            // lowercase props to the underlying <button>, so React rendered
            // enabled="<the JWT>" into the DOM — the raw bearer token was
            // visible in devtools and to any injected script. It also left the
            // button looking active while logged out, since `enabled` does
            // nothing to a button's disabled state.
            disabled={!token}
            onClick={toggleDrawer(true)}
          >
            <FontAwesomeIcon icon={faListAlt} />
          </IconButton>
          <Typography
            variant="h6"
            className={classes.title}
            onClick={() => handleRedirect('/')}
            style={{ cursor: 'pointer' }}
          >
            Package Manager App
          </Typography>

          {/* ✅ Top-right buttons */}
          {isMobile ? (
            <>
              <IconButton color="inherit" onClick={handleMenu}>
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {!token ? (
                  <MenuItem onClick={() => handleRedirect('/login')}>
                    Login
                  </MenuItem>
                ) : (
                  <>
                    <MenuItem onClick={() => handleRedirect('/profile')}>
                      Profile
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                  </>
                )}
              </Menu>
            </>
          ) : (
            <>
              {token ? (
                <>
                  <Button
                    color="inherit"
                    onClick={() => handleRedirect('/profile')}
                  >
                    Profile
                  </Button>
                  <Button color="inherit" onClick={handleLogout}>
                    Logout
                  </Button>
                </>
              ) : (
                <Button
                  color="inherit"
                  onClick={() => handleRedirect('/login')}
                >
                  Login
                </Button>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* ✅ Drawer Sidebar */}

      {token && ( // Check if token is not null
        <Drawer anchor="left" open={open} onClose={toggleDrawer(false)}>
          <div
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
            style={{ width: 250 }}
          >
            <List>
              {menuItems.map((item) => (
                <ListItem
                  button
                  key={item.text}
                  component={Link}
                  to={item.path}
                >
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
            </List>
          </div>
        </Drawer>
      )}
    </Root>
  );
}
